import F84Game from "../../Game";

export class LoaderPlugin extends Phaser.Plugins.BasePlugin {
    constructor(pluginManager: Phaser.Plugins.PluginManager) {
        super(pluginManager);

        pluginManager.registerFileType("webfont", this.webFontFileCallback);
        pluginManager.registerFileType("lottie", this.lottieFileCallback);
        pluginManager.registerFileType("spineImageAtlas", this.spineImageAtlasFileCallback);
    }

    private webFontFileCallback(key: string, url: string): Phaser.Loader.LoaderPlugin {
        const loader: Phaser.Loader.LoaderPlugin = <Phaser.Loader.LoaderPlugin>(<unknown>this);
        const file = new WebFontFile(loader, {
            type: "webfont",
            key: key,
            url: url,
        });
        loader.addFile(file);
        return loader;
    }

    private lottieFileCallback(
        key: string,
        url: string,
        width: number,
        height: number,
        spritesheet?: string,
        spritesheetData?: SpriteAtlas,
        backgroundOnly?: boolean
    ): Phaser.Loader.LoaderPlugin {
        const loader: Phaser.Loader.LoaderPlugin = <Phaser.Loader.LoaderPlugin>(<unknown>this);
        const file = new LottieFile(
            loader,
            {
                type: "lottie",
                key: key,
                url: url,
            },
            width,
            height,
            spritesheet,
            spritesheetData,
            backgroundOnly
        );
        loader.addFile(file);
        return loader;
    }

    private spineImageAtlasFileCallback(
        key: string,
        textureURL?: string,
        atlasURL?: string,
        textureXhrSettings?: Phaser.Types.Loader.XHRSettingsObject,
        atlasXhrSettings?: Phaser.Types.Loader.XHRSettingsObject
    ): Phaser.Loader.LoaderPlugin {
        const loader: Phaser.Loader.LoaderPlugin = <Phaser.Loader.LoaderPlugin>(<unknown>this);
        const multifile = new SpineImageAtlas(loader, key, textureURL, atlasURL, textureXhrSettings, atlasXhrSettings);
        loader.addFile(multifile.files);
        return loader;
    }
}

export class WebFontFile extends Phaser.Loader.File {
    load(): void {
        const nextFile = (): void => this.loader.nextFile(this, true);
        const font = new FontFaceObserver(<string>this.url);
        font.load(null, 10000).then(
            function () {
                nextFile();
            },
            function () {
                nextFile();
            }
        );
    }

    onProcess(): void {
        this.onProcessComplete();
    }
}

export class LottieFile extends Phaser.Loader.File {
    private _width: number;
    private _height: number;
    private _spritesheet: string;
    private _spritesheetData: SpriteAtlas;
    private _backgroundOnly: boolean;
    constructor(
        loader: Phaser.Loader.LoaderPlugin,
        fileConfig: Phaser.Types.Loader.FileConfig,
        width: number,
        height: number,
        spritesheet?: string,
        spritesheetData?: SpriteAtlas,
        backgroundOnly?: boolean
    ) {
        super(loader, fileConfig);
        this._width = width;
        this._height = height;
        this._spritesheet = spritesheet;
        this._spritesheetData = spritesheetData;
        this._backgroundOnly = backgroundOnly;
    }

    load(): void {
        if (this.loader.textureManager.exists(this.key)) {
            this.loader.nextFile(this, true);
            return;
        }

        //hack, shouldnt need to reference resolution in game code
        this._width *= F84Game.Instance.quality.resolution;
        this._height *= F84Game.Instance.quality.resolution;

        const div = (<any>window).document.createElement("div");
        div.style.width = this._width + "px";
        div.style.height = this._height + "px";

        (<any>window).lottie.setQuality("low"); //todo: change this based on device specs

        //Note: By setting autoplay to true and then stopping the animation on data_ready, we are able to fix any preload issues we may have when rendering these as a sprite for the first time
        <any>window.document.body.appendChild(div);
        const animation = (<any>window).lottie.loadAnimation({
            renderer: "canvas",
            context: this.loader.scene.game.canvas.getContext("2d"),
            container: div, // window.document.getElementById("content"),
            loop: false,
            autoplay: true,
            path: this.url,
            rendererSettings: {
                preserveAspectRatio: "xMidYMid meet",
                dpr: 1,
            },

            spritesheet: this._spritesheet,
            spritesheetData: this._spritesheetData,
        });
        const onDataReady = ((): void => {
            animation.stop();
            animation.removeEventListener("data_ready", onDataReady);
        }).bind(this);
        animation.addEventListener("data_ready", onDataReady);

        const onDomReady = ((): void => {
            animation.key = this.key;
            animation.name = this.key;
            animation.baseWidth = this._width;
            animation.baseHeight = this._height;
            if (this._backgroundOnly != true) {
                this.loader.textureManager.addCanvas(this.key, animation.renderer.canvasContext.canvas);
            }
            this.loader.nextFile(this, true);
            window.document.body.removeChild(div);
            animation.removeEventListener("DOMLoaded", onDomReady);
        }).bind(this);
        animation.addEventListener("DOMLoaded", onDomReady);
    }

    onProcess(): void {
        this.onProcessComplete();
    }
}

export class SpineImageAtlas extends Phaser.Loader.MultiFile {
    constructor(
        loader: Phaser.Loader.LoaderPlugin,
        key: string,
        textureURL?: string,
        atlasURL?: string,
        textureXhrSettings?: Phaser.Types.Loader.XHRSettingsObject,
        atlasXhrSettings?: Phaser.Types.Loader.XHRSettingsObject
    ) {
        const image = new Phaser.Loader.FileTypes.ImageFile(loader, key, textureURL, textureXhrSettings);
        const data = new Phaser.Loader.FileTypes.TextFile(loader, key, atlasURL, atlasXhrSettings);
        super(loader, "spineimageatlas", key, [image, data]);
    }

    addToCache(): void {
        if (this.isReadyToProcess()) {
            const image = this.files[0];
            const text = this.files[1];

            const texture = this.loader.textureManager.addImage(this.key, image.data);

            const stringData: string | undefined = text.data as string;
            if (stringData) {
                const dataLines = stringData.split("\n");
                for (let i = 4; i < dataLines.length; i++) {
                    const line = dataLines[i];
                    if (!line.includes(":") && i + 2 <= dataLines.length) {
                        const frameName = line.replace("\r", "");

                        let bounds = ["0", "0", "1", "1"];
                        let offsets: string[] = [];
                        let origin = ["0", "0"];
                        let iterator = 1;
                        while (dataLines[i + iterator] != undefined && dataLines[i + iterator].includes(":")) {
                            const dataLine = dataLines[i + iterator];
                            if (dataLine.includes("bounds")) bounds = dataLine.replace("\r", "").split(":")[1].split(",");
                            else if (dataLine.includes("offset")) offsets = dataLine.replace("\r", "").split(":")[1].split(",");
                            else if (dataLine.includes("origin")) origin = dataLine.replace("\r", "").split(":")[1].split(",");
                            iterator++;
                        }

                        if (offsets.length == 0) {
                            offsets = ["0", "0", bounds[2], bounds[3]];
                        }

                        const x: number = parseInt(bounds[0]);
                        const y: number = parseInt(bounds[1]);
                        const width: number = parseInt(bounds[2]);
                        const height: number = parseInt(bounds[3]);
                        const offsetX = parseInt(offsets[0]);
                        const offsetY = parseInt(offsets[1]);
                        const realWidth = parseInt(offsets[2]);
                        const realHeight = parseInt(offsets[3]);
                        //const originX = parseInt(origin[0]);
                        //const originY = parseInt(origin[1]);
                        const frame = texture.add(frameName, 0, x, y, width, height);
                        frame.setTrim(realWidth, realHeight, offsetX, offsetY, width, height);
                        if (origin) {
                            // Unsupported?
                            // frame.customPivot = true;
                            // frame.pivotX = originX / realWidth;
                            // frame.pivotY = originY / realHeight;
                            // console.log(frame.pivotX + ", " + frame.pivotY);
                        }
                    }
                }
            }

            text.pendingDestroy();

            this.complete = true;
        }
    }
}


//typings only
export interface Loader extends Phaser.Loader.LoaderPlugin {
    webfont(key: string, url: string, overwrite?: boolean): Phaser.Loader.LoaderPlugin;
    lottie(
        key: string,
        url: string,
        width: number,
        height: number,
        spritesheet?: string,
        spritesheetData?: SpriteAtlas,
        overwrite?: boolean
    ): Phaser.Loader.LoaderPlugin;
    gltf(key: string, url: string): Phaser.Loader.LoaderPlugin;
    spineImageAtlas(
        key: string | Phaser.Types.Loader.FileTypes.AtlasJSONFileConfig,
        textureURL?: string,
        atlasURL?: string,
        textureXhrSettings?: Phaser.Types.Loader.XHRSettingsObject,
        atlasXhrSettings?: Phaser.Types.Loader.XHRSettingsObject
    ): Phaser.Loader.LoaderPlugin;
}
